import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useBulkAssignUsersMutation } from "../../store/apis/companyApi";
import { useGetUsersQuery } from "../../store/apis/usersApi";

interface BulkAssignUsersModalProps {
  open: boolean;
  companyIds: string[];
  onClose: () => void;
  onSuccess?: () => void;
}

const BulkAssignUsersModal: React.FC<BulkAssignUsersModalProps> = ({
  open,
  companyIds,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: usersResponse, isLoading: isLoadingUsers } = useGetUsersQuery();
  const [bulkAssignUsers, { isLoading: isSubmitting }] =
    useBulkAssignUsersMutation();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const users = usersResponse?.data ?? [];
  const selectedUsers = users.filter((u) => selectedUserIds.includes(u.id));

  const handleClose = () => {
    setSelectedUserIds([]);
    onClose();
  };

  const handleConfirm = async () => {
    try {
      const response = await bulkAssignUsers({
        companyIds,
        userIds: selectedUserIds,
      }).unwrap();
      dispatch(
        setToastMessage({
          message: t("company.bulkAssign.success", {
            assignedCount: response.data.assignedCount,
            companyCount: response.data.companyCount,
          }),
          severity: "success",
        })
      );
      setSelectedUserIds([]);
      onSuccess?.();
      onClose();
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        t("company.bulkAssign.error");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="bulk-assign-users-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="bulk-assign-users-title" sx={{ pb: 2, px: 3 }}>
        <Typography variant="h5">{t("company.bulkAssign.title")}</Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            multiple
            options={users}
            loading={isLoadingUsers}
            disableCloseOnSelect
            getOptionLabel={(user) =>
              `${user.firstName} ${user.lastName} (${user.email})`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedUsers}
            onChange={(_, newValue) =>
              setSelectedUserIds(newValue.map((u) => u.id))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("company.bulkAssign.usersLabel")}
                placeholder={t("common.search", "Search…")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingUsers ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ color: (theme) => theme.palette.neutral[600] }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isSubmitting || selectedUserIds.length === 0}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          sx={{ color: "white" }}
        >
          {t("common.confirm", "Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignUsersModal;
